import { FC, useEffect, useState, useMemo } from 'react';
import { Select, Typography, Slider, Row, InputNumber, Col, Card } from 'antd';
import Plot from 'react-plotly.js';
import { DiskData, MergedFspSegment } from '../../types/turnFsp';
import { DEFAULT_PLOTLY_CONFIG, DEFAULT_PLOTLY_LAYOUT, DISK_COLORS } from '../../utils/constants/constants';
import { Layout } from 'plotly.js';
import { getDiskNameFromPosition } from '../../utils/helpers/utils';

const { Title } = Typography;

interface Props {
  computedDiameters: DiskData[];
  diskNames: string[];
  diskImages: string[];
}

interface CrossingInfo {
  angle: number;
  type: 'start' | 'stop' | 'unknown';
}

interface DiskStats {
  disk: number;
  mean: number;
  std: number;
  count: number;
  min: number;
  max: number;
}

export const BoxPlotDiameter: FC<Props> = ({ computedDiameters, diskNames, diskImages }) => {
  console.log('computedDiameters', computedDiameters);

  // Calculate statistics for each disk
  const diskStats = useMemo(() => {
    const stats: { [disk: number]: DiskStats } = {};

    // Group data by disk
    computedDiameters.forEach(({ disk, diameter }) => {
      if (!stats[disk]) {
        stats[disk] = {
          disk,
          mean: 0,
          std: 0,
          count: 0,
          min: diameter,
          max: diameter,
        };
      }
    });

    // Calculate means
    computedDiameters.forEach(({ disk, diameter }) => {
      stats[disk].mean += diameter;
      stats[disk].count += 1;
      stats[disk].min = Math.min(stats[disk].min, diameter);
      stats[disk].max = Math.max(stats[disk].max, diameter);
    });

    Object.values(stats).forEach((stat) => {
      stat.mean /= stat.count;
    });

    // Calculate standard deviations
    computedDiameters.forEach(({ disk, diameter }) => {
      stats[disk].std += Math.pow(diameter - stats[disk].mean, 2);
    });

    Object.values(stats).forEach((stat) => {
      stat.std = Math.sqrt(stat.std / stat.count);
    });

    return Object.values(stats).sort((a, b) => a.disk - b.disk);
  }, [computedDiameters]);

  // Prepare data for circular plot
  const plotData = useMemo(() => {
    const traces: any[] = [];
    const colors = DISK_COLORS;

    diskStats.forEach((stat, index) => {
      const x = index * 2; // Spread circles across width
      const y = 0; // All on same horizontal line

      // Main circle (mean diameter as size)
      const circleSize = stat.mean * 3; // Scale for visibility
      const diskName = getDiskNameFromPosition(diskNames, stat.disk);

      traces.push({
        x: [x],
        y: [y],
        mode: 'markers',
        marker: {
          size: circleSize,
          color: colors[index % colors.length],
          opacity: 0.6,
          line: {
            color: colors[index % colors.length],
            width: 2,
          },
        },
        name: diskName,
        text: [`${diskName}<br>` + `Mean: ${stat.mean.toFixed(1)} mm<br>` + `Std: ±${stat.std.toFixed(1)} mm<br>` + `Range: ${stat.min.toFixed(1)} - ${stat.max.toFixed(1)} mm<br>` + `Count: ${stat.count}`],
        hovertemplate: '%{text}<extra></extra>',
        type: 'scatter',
      });
    });

    return traces;
  }, [diskStats, computedDiameters, diskNames]);

  const layout: Partial<Layout> = {
    ...DEFAULT_PLOTLY_LAYOUT,
    title: {
      text: 'Disk Diameter Analysis',
      font: { size: 16, color: '#262626' },
    },
    xaxis: {
      title: 'Antibiotic Position',
      showgrid: false,
      zeroline: false,
      tickmode: 'array' as const,
      tickvals: [0, 2, 4, 6],
      ticktext: diskStats.map(() => ''),
      range: [-0.5, (diskStats.length - 1) * 2 + 0.5],
    },
    yaxis: {
      showgrid: false,
      zeroline: false,
      showticklabels: false,
      range: [-0.3, 0.3],
    },
    hovermode: 'closest' as const,
    showlegend: true,
    legend: {
      orientation: 'h' as const,
      y: -0.2,
      x: 0.5,
      xanchor: 'center' as const,
    },
    images: diskStats.map((stat, index) => {
      return {
        source: diskImages[index] ? 'data:image/png;base64,' + diskImages[index] : undefined,
        xref: 'x',
        yref: 'y',
        x: index * 2,
        y: 0.015,
        sizex: 0.5,
        sizey: 0.5,
        xanchor: 'center',
        yanchor: 'middle',
        layer: 'above',
      };
    }),
    annotations: diskStats.map((stat, index) => ({
      x: index * 2,
      y: -0.15,
      text: `<span style="font-size: 24px">⌀</span><br><b style="font-size: 18px">${stat.mean.toFixed(1)} mm</b><br><span style="font-size: 14px">±${stat.std.toFixed(1)} mm</span>`,
      showarrow: false,
      font: {
        size: 14,
        color: '#333',
      },
      bgcolor: 'rgba(255,255,255,0.9)',
      bordercolor: '#d9d9d9',
      borderwidth: 1,
      borderpad: 8,
    })),
  };

  return (
    <div>
      <Card
        title="Antibiotic Diameter Variability"
        bordered
        style={{
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          marginBottom: 16,
        }}
      >
        <Plot data={plotData} layout={layout} config={DEFAULT_PLOTLY_CONFIG} style={{ width: '100%', height: '500px' }} />

        {/* Statistics Summary */}
        <Row gutter={16} style={{ marginTop: 16 }}>
          {diskStats.map((stat, index) => (
            <Col span={6} key={stat.disk}>
              <Card
                size="small"
                style={{
                  textAlign: 'center',
                  borderLeft: `4px solid ${['#1890ff', '#52c41a', '#fa8c16', '#eb2f96'][index]}`,
                }}
              >
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {getDiskNameFromPosition(diskNames, stat.disk)}
                </Typography.Title>
                <Typography.Text strong style={{ fontSize: '18px' }}>
                  {stat.mean.toFixed(1)} mm
                </Typography.Text>
                <br />
                <Typography.Text type="secondary">±{stat.std.toFixed(2)} mm</Typography.Text>
                <br />
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  CV: {((stat.std / stat.mean) * 100).toFixed(1)}%
                </Typography.Text>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
    </div>
  );
};
